/**
 * @file Lazy-loaded Stripe
 */

import { lazy } from "@redotech/util/cache";
import { Stripe } from "@stripe/stripe-js";
import { ReactNode, createContext, useMemo } from "react";

export type StripeFn = () => Promise<Stripe | null>;

export const StripeContext = createContext<StripeFn | undefined>(undefined);

const stripeByPublicKey = new Map<string, StripeFn>();

function stripeForPublicKey(publicKey: string): StripeFn {
  const cached = stripeByPublicKey.get(publicKey);
  if (cached) {
    return cached;
  }
  const stripe = lazy(async () => {
    const { loadStripe } = await import("@stripe/stripe-js/pure");
    return await loadStripe(publicKey);
  });
  stripeByPublicKey.set(publicKey, stripe);
  return stripe;
}

/**
 * Nest a provider with another account's publishable key to scope Elements
 * under it to that Stripe account.
 */
export function StripeProvider({
  children,
  publicKey,
}: {
  children?: ReactNode | ReactNode[];
  publicKey: string;
}) {
  const stripePromise = useMemo(
    () => stripeForPublicKey(publicKey),
    [publicKey],
  );
  return (
    <StripeContext.Provider value={stripePromise}>
      {children}
    </StripeContext.Provider>
  );
}
